'use client'

import { useState, useRef, useEffect } from 'react'
import classNames from '@/utils/classNames'
import withHeaderItem from '@/utils/hoc/withHeaderItem'
import Button from '@/components/ui/Button'
import Dialog from '@/components/ui/Dialog'
import ScrollBar from '@/components/ui/ScrollBar'
import debounce from 'lodash/debounce'
import { HiOutlineSearch, HiChevronRight } from 'react-icons/hi'
import { PiMagnifyingGlassDuotone } from 'react-icons/pi'
import Link from 'next/link'
import Highlighter from 'react-highlight-words'
import { apiSearchProducts } from '@/lib/searchProducts'
import { useSearchParams, usePathname } from 'next/navigation'
import { useRouter } from 'next/navigation'

type SearchData = {
    key: string
    path: string
    title: string
    local_main_image: string
}

type SearchResult = {
    title: string
    data: SearchData[]
}

const recommendedSearch: SearchResult[] = [
    {
        title: 'Рекомендуем',
        data: [],
    },
]

const ListItem = (props: {
    local_main_image: string
    label: string
    url: string
    isLast?: boolean
    keyWord: string
    onNavigate: () => void
}) => {
    const { local_main_image, label, url = '', keyWord, onNavigate } = props

    const isLocal = process.env.NEXT_PUBLIC_API_ENV === 'local'

    const IMG_URL = isLocal ? process.env.NEXT_PUBLIC_IMG_URL : '/img'

    return (
        <Link href={url} onClick={onNavigate}>
            <div
                className={classNames(
                    'flex items-center justify-between rounded-xl p-3 cursor-pointer user-select',
                    'hover:bg-gray-100 dark:hover:bg-gray-700',
                )}
            >
                <div className="flex items-center gap-2">
                    <div
                        className={classNames(
                            'rounded-lg border-2 border-gray-200 shadow-xs text-xl group-hover:shadow-sm h-10 w-10 flex items-center justify-center bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100',
                        )}
                    >
                        <img
                            src={
                                `${IMG_URL}/${local_main_image}` ||
                                '/img/others/upload.png'
                            }
                            alt="img"
                            className="w-full h-full object-cover"
                        />
                        {/* <Image
    src={main_image}
    alt={label}
    width={32}
    height={32}
    className="rounded object-cover"
/> */}
                    </div>
                    <div className="text-gray-900 dark:text-gray-300">
                        <Highlighter
                            autoEscape
                            highlightClassName={classNames(
                                'text-primary',
                                'underline bg-transparent font-semibold dark:text-white',
                            )}
                            searchWords={[keyWord]}
                            textToHighlight={label}
                        />
                    </div>
                </div>
                <HiChevronRight className="text-lg" />
            </div>
        </Link>
    )
}

const _Search = ({ className }: { className?: string }) => {
    const [searchDialogOpen, setSearchDialogOpen] = useState(false)

    const [searchResult, setSearchResult] = useState<SearchResult[]>([])

    const [noResult, setNoResult] = useState(false)

    const router = useRouter()

    const inputRef = useRef<HTMLInputElement>(null)

    const handleReset = () => {
        setNoResult(false)
        setSearchResult([])
    }

    const handleSearchOpen = () => {
        setSearchDialogOpen(true)
    }

    const handleSearchClose = () => {
        setSearchDialogOpen(false)
        handleReset()
    }

    const handleSubmit = () => {
        const query = inputRef.current?.value?.trim()
        if (!query) return

        handleSearchClose()
        router.push(`/search?q=${encodeURIComponent(query)}`)
    }
    const mapProductsToSearchResult = (products: any[]): SearchResult => ({
        title: 'Товары',
        data: products.map((product) => ({
            key: product.product_id,
            title: product.name,
            latin: product.latin,
            path: `/catalog/product/${product.latin}`,
            local_main_image: product.local_main_image,
        })),
    })
    const debounceFn = debounce(handleDebounceFn, 500)

    async function handleDebounceFn(query: string) {
        if (!query) {
            setSearchResult([])
            return
        }

        if (noResult) {
            setNoResult(false)
        }

        try {
            const response = await apiSearchProducts({
                q: query,
                limit: 5,
            })

            if (!response.data.length) {
                setNoResult(true)
                setSearchResult([])
                return
            }

            setNoResult(false)
            setSearchResult([mapProductsToSearchResult(response.data)])
        } catch (error: any) {
            setNoResult(true)

            setSearchResult([])
        }
    }

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        debounceFn(e.target.value)
    }

    useEffect(() => {
        if (searchDialogOpen) {
            const timeout = setTimeout(() => inputRef.current?.focus(), 100)
            return () => {
                clearTimeout(timeout)
            }
        }
    }, [searchDialogOpen])

    const handleNavigate = () => {
        handleSearchClose()
    }

    const searchParams = useSearchParams()
    const pathname = usePathname()

    const queryFromUrl = searchParams.get('q') ?? ''
    const isSearchPage = pathname === '/search'

    return (
        <>
            <div
                className={classNames(className, 'text-base')}
                onClick={handleSearchOpen}
            >
                <PiMagnifyingGlassDuotone className="text-2xl" />
                <div>
                    <span className="truncate max-w-[140px]">
                        {isSearchPage && queryFromUrl ? queryFromUrl : 'Поиск'}
                    </span>
                </div>
            </div>
            <Dialog
                contentClassName="p-0"
                isOpen={searchDialogOpen}
                closable={false}
                onRequestClose={handleSearchClose}
            >
                <div>
                    <div className="px-4 flex items-center justify-between border-b border-gray-200 dark:border-gray-600">
                        <div className="flex items-center">
                            <HiOutlineSearch className="text-xl" />
                            <input
                                ref={inputRef}
                                className="ring-0 outline-hidden block w-full p-4 text-base bg-transparent text-gray-900 dark:text-gray-100"
                                placeholder="Поиск..."
                                onChange={handleSearch}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault()
                                        handleSubmit()
                                    }
                                }}
                            />
                        </div>
                        <Button size="xs" onClick={handleSearchClose}>
                            X
                        </Button>
                    </div>
                    <div className="py-6 px-5">
                        <ScrollBar className=" max-h-[350px] overflow-y-auto">
                            {searchResult.map((result) => (
                                <div key={result.title} className="mb-4">
                                    <h6 className="mb-3">{result.title}</h6>
                                    {result.data.map((data, index) => (
                                        <ListItem
                                            key={data.title + index}
                                            local_main_image={
                                                data.local_main_image
                                            }
                                            label={data.title}
                                            url={data.path}
                                            keyWord={
                                                inputRef.current?.value || ''
                                            }
                                            onNavigate={handleNavigate}
                                        />
                                    ))}
                                </div>
                            ))}
                            {searchResult.length === 0 && noResult && (
                                <div className="my-10 text-center text-lg">
                                    <span>Ничего не найдено </span>
                                    <span className="heading-text">
                                        {`'`}
                                        {inputRef.current?.value}
                                        {`'`}
                                    </span>
                                </div>
                            )}

                            {/* {searchResult.length > 0 && (
                                <div className="mt-4 text-center">
                                    <Link
                                        href={`/search?q=${encodeURIComponent(inputRef.current?.value || '')}`}
                                        className="button flex gap-2 items-center justify-center bg-primary text-white hover:bg-pimary-hover  w-[120px] rounded-xl px-8 py-4 text-sm"
                                        onClick={handleSearchClose}
                                    >
                                        Смотреть все
                                    </Link>
                                </div>
                            )} */}
                        </ScrollBar>
                        {inputRef.current?.value && (
                            <div className="pt-4 border-t">
                                <button
                                    onClick={handleSubmit}
                                    className="w-full bg-primary text-white rounded-xl py-3 text-sm hover:bg-primary-hover transition"
                                >
                                    Смотреть все
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>
        </>
    )
}

const Search = withHeaderItem(_Search)

export default Search
